import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid2,
  Paper,
  Avatar,
  Chip,
  Button,
  Tabs,
  Tab,
  IconButton,
  Badge,
  Alert,
  CircularProgress,
  alpha,
} from '@mui/material';
import BasePage from './BasePage';
import { useBusiness } from '../contexts/BusinessContext';
import { useBusinessAgent } from '../contexts/BusinessAgentContext';
import { useAuth } from '../contexts/AuthContext';
import { useClientManagement } from '../hooks/useClientManagement';
import { useAgentToClientChat } from '../hooks/useAgentToClientChat';
import { ClientData, ClientHome } from '../types/business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import MessageIcon from '@mui/icons-material/Message';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClientChat from '../components/business/ClientChat';
import ClientHomesTable from '../components/business/ClientHomesTable';

const ClientProfile = () => {
  const navigate = useNavigate();
  const { clientEmail } = useParams<{ clientEmail: string }>();
  const { business } = useBusiness();
  const { isAgent } = useBusinessAgent();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  
  // Get the decoded client email for the chat hook
  const decodedClientEmail = useMemo(() => {
    return clientEmail ? decodeURIComponent(clientEmail) : '';
  }, [clientEmail]);
  
  // Use the real agent-to-client chat hook
  const {
    messages,
    isLoading: isChatLoading,
    isConnected: isChatConnected,
    unreadCount: unreadMessagesCount,
    connectionError: chatError,
    sendMessage,
    markAsRead: markMessagesAsRead,
    connect: connectChat,
    disconnect: disconnectChat,
    sendTyping
  } = useAgentToClientChat({ 
    clientEmail: decodedClientEmail,
    autoConnect: false // We'll connect manually when the chat tab is opened
  });

  // Use client management hook to get client data and homes
  const {
    clients,
    isLoadingClients,
    clientsError,
    fetchClients,
    clientHomes,
    isLoadingClientHomes,
    clientHomesError,
    fetchClientHomes
  } = useClientManagement({
    businessId: business?.id,
    agentEmail: user?.email
  });

  // Find the specific client by email
  const client = useMemo(() => {
    if (!clientEmail || !clients.length) return null;
    
    const decodedEmail = decodeURIComponent(clientEmail);
    return clients.find(c => c.email === decodedEmail) || null;
  }, [clientEmail, clients]);

  // Get homes for this specific client
  const clientHomesData = useMemo((): ClientHome[] => {
    if (!clientEmail) return [];
    const decodedEmail = decodeURIComponent(clientEmail);
    return clientHomes[decodedEmail] || [];
  }, [clientEmail, clientHomes]);

  // Enhanced client data with mock preferences and notes
  const enhancedClient = useMemo((): (ClientData & { preferences: string; notes: string }) | null => {
    if (!client) return null;
    
    return {
      ...client,
      // Mock data - clearly indicated as such
      preferences: 'Looking for a 3-bedroom home with a large backyard, preferably in a quiet neighborhood with good schools nearby. Budget range: $300k-$450k. (Mock data for demo)',
      notes: 'First-time homebuyer. Very detail-oriented and asks great questions. Prefers evening appointments. Highly engaged with CribCritic analysis reports. (Mock data for demo)'
    };
  }, [client]);

  useEffect(() => {
    if (business?.id && user?.email && isAgent) {
      fetchClients();
    }
  }, [business?.id, user?.email, isAgent, fetchClients]);

  // Fetch client homes when client is found
  useEffect(() => {
    if (client && clientEmail) {
      const decodedEmail = decodeURIComponent(clientEmail);
      fetchClientHomes(decodedEmail);
    }
  }, [client, clientEmail, fetchClientHomes]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBackToClients = () => {
    navigate('/business/agent');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  // Auto-connect to chat when the chat tab is viewed
  useEffect(() => {
    if (activeTab === 1 && decodedClientEmail && !isChatConnected && !isChatLoading) {
      console.log('Connecting to chat for client:', decodedClientEmail);
      connectChat();
    }
  }, [activeTab, decodedClientEmail, isChatConnected, isChatLoading, connectChat]);

  // NEW: disconnect when the chat tab is left
  useEffect(() => {
    if (activeTab !== 1 && isChatConnected) {
      console.log('Leaving chat tab – disconnecting WebSocket');
      disconnectChat();
    }
  }, [activeTab, isChatConnected, disconnectChat]);

  // Disconnect chat when leaving the page
  useEffect(() => {
    return () => {
      disconnectChat();
    };
  }, [disconnectChat]);

  if (!isAgent) {
    return (
      <BasePage
        title="Access Denied | CribCritic"
        description="Agent access required"
      >
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Alert severity="error">
            You need agent access to view client profiles.
          </Alert>
        </Container>
      </BasePage>
    );
  }

  if (isLoadingClients) {
    return (
      <BasePage
        title="Loading Client Profile | CribCritic"
        description="Loading client information"
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading client profile...
              </Typography>
            </Box>
          </Box>
        </Container>
      </BasePage>
    );
  }

  if (clientsError || !enhancedClient) {
    return (
      <BasePage
        title="Client Not Found | CribCritic"
        description="The requested client profile could not be found"
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" gutterBottom>
              Client Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {clientsError || "The client profile you're looking for doesn't exist or you don't have access to it."}
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleBackToClients}
              startIcon={<ArrowBackIcon />}
            >
              Back to Clients
            </Button>
          </Box>
        </Container>
      </BasePage>
    );
  }

  return (
    <BasePage
      title={`${enhancedClient.name} - Client Profile | CribCritic`}
      description={`Manage client ${enhancedClient.name} and view their property analysis progress`}
      keywords="client profile, agent dashboard, property analysis"
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton 
              onClick={handleBackToClients}
              sx={{ 
                mr: 1,
                position: 'relative',
                zIndex: 1400
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Client Profile
            </Typography>
          </Box>
          
          {/* Client Header Card */}
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 2,
              background: theme => `linear-gradient(135deg, 
                ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Grid2 container spacing={3} alignItems="center">
              <Grid2 size={{ xs: 12, md: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Avatar 
                    src={enhancedClient.avatar || enhancedClient.user?.picture} 
                    alt={enhancedClient.name}
                    sx={{ 
                      width: 80, 
                      height: 80,
                      bgcolor: 'primary.main',
                      fontSize: '1.5rem'
                    }}
                  >
                    {enhancedClient.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  
                  <Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {enhancedClient.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip 
                        label={enhancedClient.status} 
                        color={getStatusColor(enhancedClient.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                      <Chip 
                        label={`${enhancedClient.stats?.activeHomes || enhancedClient.activeHomes || 0} Active Properties`}
                        color="info"
                        size="small"
                      />
                      {enhancedClient.user && (
                        <Chip 
                          label="CribCritic User"
                          color="success"
                          size="small"
                          icon={<PersonIcon />}
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon fontSize="small" />
                        {enhancedClient.email}
                      </Typography>
                      {enhancedClient.phone && (
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="small" />
                          {enhancedClient.phone}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon fontSize="small" />
                        Client since {formatDate(enhancedClient.joinedDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid2>
              
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Grid2 container spacing={2}>
                  <Grid2 size={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold" color="primary.main">
                        {enhancedClient.stats?.totalHomes || enhancedClient.totalHomes || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Homes
                      </Typography>
                    </Box>
                  </Grid2>
                  <Grid2 size={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {enhancedClient.stats?.activeHomes || enhancedClient.activeHomes || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active
                      </Typography>
                    </Box>
                  </Grid2>
                  <Grid2 size={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {enhancedClient.stats?.homesThisMonth || enhancedClient.homesThisMonth || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This Month
                      </Typography>
                    </Box>
                  </Grid2>
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
        </Box>

        {/* Client Details Section */}
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Client Preferences
                <Chip 
                  label="Mock Data" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 1, fontSize: '0.7rem' }}
                />
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {enhancedClient.preferences}
              </Typography>
            </Paper>
          </Grid2>
          
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Agent Notes
                <Chip 
                  label="Mock Data" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 1, fontSize: '0.7rem' }}
                />
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {enhancedClient.notes}
              </Typography>
            </Paper>
          </Grid2>
        </Grid2>

        {/* Tab Navigation */}
        <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="client profile tabs">
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon />
                  Properties ({clientHomesData.length})
                  {clientHomesError && (
                    <Chip 
                      label="Error" 
                      size="small" 
                      color="error" 
                      sx={{ ml: 0.5, fontSize: '0.6rem', height: '16px' }}
                    />
                  )}
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MessageIcon />
                  Messages
                  {unreadMessagesCount > 0 && (
                    <Badge 
                      badgeContent={unreadMessagesCount} 
                      color="error" 
                      sx={{ ml: 0.5 }}
                    />
                  )}
                </Box>
              } 
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <>
            {clientHomesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error loading client homes: {clientHomesError}
              </Alert>
            )}
            <ClientHomesTable 
              homes={clientHomesData}
              isLoading={isLoadingClientHomes}
              clientName={enhancedClient.name}
              clientEmail={decodedClientEmail}
            />
          </>
        )}

        {activeTab === 1 && (
          <>
            {chatError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Chat connection error: {chatError}
              </Alert>
            )}
            <ClientChat
              messages={messages}
              isLoading={isChatLoading}
              onSendMessage={sendMessage}
              onMarkAsRead={markMessagesAsRead}
              onSendTyping={sendTyping}
              clientName={enhancedClient.name}
              agentName={enhancedClient.agent?.name || "Agent"}
            />
          </>
        )}
      </Container>
    </BasePage>
  );
};

export default ClientProfile;